import { Component, Input, OnInit, DoCheck, Output, EventEmitter } from '@angular/core';
import { XsI18nPipe, FileSizeFormatPipe } from '@nx-fe/pipes';
import {
  ErrorHandlerService,
  FeatureToggleService,
  GlobalEventBusService,
  hd,
  LicenceInfoService,
  LicenceStatus,
  LoggerService,
  TicketContextService,
  TicketService,
} from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { AttachmentService } from 'app/modules/ticket/services/attachment.service';
import { ActivityContextService } from 'xsmodules/ticket/components/ticket-details-panel/activity/activity-context';
import { DeviceCaptureActivity, ActivityTypeEnum, AttachmentActivity } from 'xsmodules/ticket/ticket.model';
import { DeviceCaptureActivityTypes } from 'xsmodules/ticket/ticket-page.service';

@Component({
  selector: 'xs-device-capture-activity',
  templateUrl: './device-capture-activity.component.html',
  styleUrls: ['./device-capture-activity.component.scss'],
})
export class DeviceCaptureActivityComponent implements OnInit {
  /** The activity for this attachment, set during controller initialization */
  @Input() activity: DeviceCaptureActivity;

  /** Indicates that we are calling that component in Self Service Portal */
  @Input()
  public isSPPMode: boolean;

  /** Indicates whether the note activity is editable or not */
  @Input()
  public canEdit: boolean;

  /** The ActivityController exposes methods for saving, deleting, and updating activities */
  @Input() activityCtrl: ActivityContextService;

  @Output()
  isEditting = new EventEmitter<boolean>();

  /** Callback invoke to delete the attachment */
  public onDelete: Function;

  /** Callback to invoke when an error occurs  */
  public onError: Function;

  /** Error encountered during sending the email */
  public error: string;

  /** Device Name */
  public deviceName: string;

  /** Current upload progress (if applicable) */
  public progress = 0;

  public iconTemplate: string;

  public licenceInfo: hd.ILicenceDetails;

  /** Boolean indicating whether a failed operation can be retried */
  public canRetry: boolean;

  public captureDescription: string;

  public isShowMore = true;

  private _islicenceExpired = true;

  private noteContentHeight = 20;

  private activityIconTemplate = {
    deviceCapture: 'hd-icon hd-icon-deviceCapture',
    recordVideo: 'hd-icon hd-icon-recordVideo',
    screenCapture: 'hd-icon hd-icon-screencap',
    adbLogs: 'hd-icon hd-icon-downloadlogs',
    snapshot: 'hd-icon hd-icon-deviceinfo',
  };

  /** Sets up a watch on the activity ID so that the activity can be updated */
  constructor(
    private attachmentService: AttachmentService,
    private $filter: XsI18nPipe,
    private ticketService: TicketService,
    private errorHandlerService: ErrorHandlerService,
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    private licenceInfoService: LicenceInfoService,
    private $log: LoggerService,
    private globalEventBusService: GlobalEventBusService,
    private fileSizeFormatter: FileSizeFormatPipe
  ) {}

  /** Called when the component and its bindings are initialized */
  ngOnInit() {
    if (this.isActivityInitialised()) {
      if (typeof this.activity.details === 'object' && Object.keys(this.activity.details).includes('0')) {
        this.activity.details = this.activity.details[0];
      }
    } else {
      this.initialiseActivity();
    }
    if (this.activity.details.deviceName) {
      this.setupCustomIconCallback();
    }
    this.captureDescription = this.getCaptureDescription();

    this.updateActivityState();

    this.licenceInfoService.getLicenceInformation().subscribe(l => (this.licenceInfo = l as any));

    // Set initial activity state
    this.updateActivityState();

    if (this.licenceInfo.status === LicenceStatus.active) {
      this._islicenceExpired = false;
    }
  }

  ngDoCheck(): void {
    if (this.activity?.details.$$isDraft === false) {
      this.captureDescription = this.getCaptureDescription();
      //this.activity.details.$$isDraft = undefined;
    }
  }

  public toggleShowMore = () => {
    this.isShowMore = !this.isShowMore;
  };

  public isLargeDescription = (acId: string): boolean => {
    const elementNote = document.getElementById('Note_Div_' + acId);
    return elementNote && elementNote.offsetHeight >= this.noteContentHeight ? true : false;
  };

  public showDescription = (): boolean => {
    return this.captureDescription && this.captureDescription !== '';
  };

  /** Is this device capture activity only contains 1 attachment and which are video or image */
  public isSingleVideoOrImageCapture = (): boolean => {
    return (
      this.iconTemplate === this.activityIconTemplate.recordVideo ||
      this.iconTemplate === this.activityIconTemplate.screenCapture
    );
  };

  /** Deletes the attachment and associated activity */
  public delete = (): void => {
    if (!this.isNew()) {
      // Delete the activity from the server.
      this.$log.debug('Deleting activity', this.activity.activityId);

      // Call delete from the ticketActivitiesResource instead of on the activity controller (which calls activity.$delete),
      // since the activity may not be a real $resource (if it was created as a local attachment)
      // const ticketActivitiesResource = this.ticketService.getTicketActivitiesResource(this.ticketContextService.ticket.ticketId);
      this.ticketService.deleteActivity(this.activity.activityId, this.ticketContextService.ticket.ticketId).subscribe(
        () => {
          this.$log.debug('Activity deleted');
          // Removes the activity from our parent's list and results in the views
          // being updated and our controller instance unloaded.
          this.activityCtrl.delete();

          // Broadcast the deletion to allow additional cleanup (such as closing file preview)
          this.globalEventBusService.broadcast(GlobalEvents.activityDeleted, this.activity);
        },
        (err: any) => {
          this.$log.debug('Failed to delete activity');
          this.$log.error(err);
          this.setError(this.$filter.transform('attachment_DeleteActivity_Error'), false);
        }
      );
    } else {
      this.activityCtrl.delete();
    }
  };

  public initialiseActivity = () => {
    this.activity.details = {
      deviceId: 'unknown',
      deviceName: 'unknown',
      mcConnectionId: NaN,
      mcConnectionName: '',
      isDefaultConnection: false,
      status: 0,
      captureType: DeviceCaptureActivityTypes.file,
      deviceFamily: '',
      captureDescription: '',
      $$isDraft: true,
      $$previewId: null,
    };
    if (this.activity.activityTypeId === ActivityTypeEnum.deviceScreenshot) {
      this.activity.details.captureType = DeviceCaptureActivityTypes.image;
    } else if (this.activity.activityTypeId === ActivityTypeEnum.deviceVideo) {
      this.activity.details.captureType = DeviceCaptureActivityTypes.video;
    }
  };

  //we set filedata to undefined because it caused the screencapture description not to save
  private updateActivity = (activityModel: AttachmentActivity) => {
    this.error = '';
    const file = this.activity.details?.files.length > 0 ? this.activity.details.files[0] : null;
    const temp = Object.assign(
      {
        activityType: ActivityTypeEnum[ActivityTypeEnum.attachment],
        fileName: file?.fileName,
        fileSize: file?.fileSize || 0,
      },
      activityModel,
      { fileData: undefined }
    );
    this.ticketService
      .saveActivity((<unknown>temp) as hd.IAttachmentActivity, this.ticketContextService.ticket.ticketId)
      .subscribe(
        res => (this.activity.description = ((<unknown>res) as hd.IAttachmentActivity).description),
        err => {
          console.error('updateActivity:', err);
          this.setError(this.$filter.transform('note_Save_Error'), false);
        }
      );
    this.updateActivityState();
  };

  /** Updates the activity state based on various attributes of the activity, model, and template */
  public updateActivityState = () => {
    let state: string;
    if (this.error) {
      state = 'error';
    } else {
      state = 'inactive';
    }
    this.activityCtrl.setActivityState(state);
  };

  public updateEditing(event) {
    this.isEditting.emit(event);
    this.updateActivityState();
  }

  /** Determines whether this is a new activity or not */
  public isNew = (): boolean => {
    return this.activity.activityId == null;
  };

  public isActivityInitialised = (): boolean => {
    return this.activity?.details != null;
  };

  /** Wehther the user is able to edit the note */
  public canEditNote = (): boolean => {
    // Always allow new notes to be edited (because this is actually a 'create' - not an edit)
    if (!this._islicenceExpired && this.activity && this.activity.activityId === undefined) {
      return true;
    }

    return this.canEdit;
  };

  /** Clears the current error */
  private clearError = (): void => {
    this.setError(undefined);
    this.errorHandlerService.clearError();
    this.updateActivityState();
  };

  private setError(err: string, canRetry: boolean = true) {
    this.error = err;
    this.canRetry = canRetry;

    if (this.error) {
      this.activityCtrl.setActivityState('error');
    }
    this.updateActivityState();
  }

  private setupCustomIconCallback = () => {
    this.activity.getCustomIconClass = () => {
      this.iconTemplate = this.activityIconTemplate.deviceCapture;

      // Now Device Capture Activity only SUPPOSE to have two kinds of child activities, image/video and note.
      // So we determine the icon based on img/video
      // In the future if this activity contains more than two kinds of children, we can use new icons to display.
      switch (this.activity.details.captureType) {
        case DeviceCaptureActivityTypes.video:
        case DeviceCaptureActivityTypes.audiovideo:
          this.iconTemplate = this.activityIconTemplate.recordVideo;
          break;
        case DeviceCaptureActivityTypes.image:
          this.iconTemplate = this.activityIconTemplate.screenCapture;
          break;
        case DeviceCaptureActivityTypes.adbLogs:
          this.iconTemplate = this.activityIconTemplate.adbLogs;
          break;
        case DeviceCaptureActivityTypes.snapshot:
          this.iconTemplate = this.activityIconTemplate.snapshot;
          break;
      }

      return this.iconTemplate;
    };
  };

  private getCaptureDescription = (): string => {
    if (this.activity.details.captureDescription) {
      return this.activity.details.captureDescription;
    }

    // if nothing is sent from the server, get the descri0ption for the logs listing parent folders
    return this.activity.activityTypeId !== ActivityTypeEnum.deviceLog
      ? ''
      : this.activity.details.files
          ?.map(f => f.fileName.split('/')[0])
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort((x, y) => x.localeCompare(y))
          .join(', ');
  };

  /** Uploads the selected file to the server */
  private uploadFile = (attachmentDraft: hd.IAttachmentActivity) => {
    this.progress = 0;
    const uploadPromise = this.attachmentService.uploadAttachment(attachmentDraft.$$file, attachmentDraft.displayName);
    uploadPromise.subscribe(
      (result: hd.IUploadResult) => {
        console.log('file uploaded', attachmentDraft);
        // TODO implement
        // const attachmentActivity = this.ticketService
        //   .getTicketActivitiesResource(this.ticketContextService.ticket.ticketId)
        //   .create(<hd.IAttachmentActivity>result.data) as hd.IAttachmentActivity;
        // this.progress = 100;
        // attachmentActivity.$mcInstanceName = this.activity.mcConnectionName;
        // if (attachmentActivity !== attachmentDraft) {
        //   // We need to save onUploadComplete and then assign to attachmentDraft,
        //   // because if we do not do this after angular.copy we will lose it.
        //   const onUploadCompleteFunction = attachmentDraft.onUploadComplete;
        //   Object.assign(this.activity, attachmentDraft);
        //   attachmentDraft.onUploadComplete = onUploadCompleteFunction;
        //   if (attachmentDraft.onUploadComplete) {
        //     attachmentDraft.onUploadComplete(hd.UploadCompleteReason.Success);
        //   }
        // }
        this.clearError();
      },
      (err: hd.IError) => {
        // const reason: hd.UploadCompleteReason = hd.UploadCompleteReason.Error;
        // Upload failed unexpectedly
        this.$log.debug('Upload failed for file: ' + attachmentDraft.$$file.name);
        if (err.status === 413 || err.status === 403) {
          this.setError(err.error_description);
        } else {
          const message = this.$filter.transform(
            'attachment_FileSizeExceeded_Error',
            attachmentDraft.$$file.name,
            this.fileSizeFormatter.transform(2000000000)
          );
          // XTODO
          //  this.$filter('i18n')(
          //   'attachment_Upload_Error',
          //   attachmentDraft.$$file.name,
          //   this.$filter('saFileSizeFormat')(attachmentDraft.$$file.size)
          this.setError(message);
        }
        err.isHandled = true;
      }
    );
    /*     (evt: any) => {
           // Update the attachment activity progress
           this.progress = Math.min(100, (100 * evt.loaded) / evt.total);
         }
       )
     */
    return attachmentDraft;
  };

  private saveNote(event: AttachmentActivity) {
    this.updateActivity(event);
  }
}
